import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Heart, Star, Target, Users, Zap, MessageCircle, Globe, Award } from 'lucide-react';
import { useCmsContent } from '../hooks/useCmsContent';
import { useLanguage } from '../contexts/LanguageContext';
import { parseHighlight } from '../utils/richText';

const ABOUT_IMAGE_FB = "/assets/photos/site-updates/pronunciation.webp";
const ABOUT_IMAGE_PREVIOUS = "/assets/photos/site-updates/classroom-teachers-reference.webp";
const METHOD_IMAGE_FB = "/assets/photos/site-updates/speaking-development.webp";
const METHOD_IMAGE_PREVIOUS = "/assets/photos/site-updates/teaching-small-groups.webp";
// Sentinel only — compared against, never rendered. Must stay exactly equal to the
// value previously stored in the CMS by an old migration so `.includes()` below
// still recognizes it and swaps in the current fallback.
const METHOD_IMAGE_LEGACY = "https://images.unsplash.com/photo-1653669487404-09c3617c2b6c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwyfHxtdWx0aWN1bHR1cmFsJTIwc3R1ZGVudHMlMjBzdHVkeWluZyUyMGxhcHRvcCUyMGhhcHB5fGVufDB8fHx8MTc3NDM5OTczOHww&ixlib=rb-4.1.0&q=85";
// Real student photos (small compressed thumbnails), replacing the generic
// placeholder avatars — the CMS "avatar" fields still override these with a
// real upload if an admin sets one.
const ABOUT_AVATARS_FB = [
  "/assets/photos/testimonials/avatars/taeok.webp",
  "/assets/photos/testimonials/avatars/cintia.webp",
  "/assets/photos/testimonials/avatars/francielly.webp",
];

const DEFAULT_DIFF = [
  { text: 'Personalized learning paths',        icon: Target,       color: 'text-[#D72B2B]' },
  { text: 'Real communication focus',           icon: MessageCircle, color: 'text-[#4CAF50]' },
  { text: 'Certified and experienced teaching', icon: Award,        color: 'text-[#1A2E6E]' },
  { text: 'Flexible learning experience',       icon: Zap,          color: 'text-[#F5A623]' },
  { text: 'Clear progress support',             icon: Star,         color: 'text-[#D72B2B]' },
  { text: 'Warm, confidence-building environment', icon: Heart,     color: 'text-[#4CAF50]' }
];

const DEFAULT_VALUES = [
  { icon: Heart,       title: 'Authenticity',  description: 'Genuine connections and honest feedback',        color: 'bg-[#D72B2B]' },
  { icon: Users,       title: 'Empathy',       description: 'Understanding your unique learning journey',     color: 'bg-[#4CAF50]' },
  { icon: Zap,         title: 'Confidence',    description: 'Building your ability to communicate freely',    color: 'bg-[#F5A623]' },
  { icon: Star,        title: 'Excellence',    description: 'High standards in everything we do',             color: 'bg-[#1A2E6E]' },
  { icon: Target,      title: 'Growth',        description: 'Continuous improvement and progress',            color: 'bg-[#D72B2B]' },
  { icon: MessageCircle, title: 'Communication', description: 'Clear, effective, and meaningful exchange',  color: 'bg-[#4CAF50]' }
];

const DEFAULT_METHOD_POINTS = [
  { text: 'Communicative approach focused on real-world use', color: 'bg-[#D72B2B]' },
  { text: 'Structured lessons with clear learning outcomes',  color: 'bg-[#4CAF50]' },
  { text: 'Regular feedback and progress tracking',          color: 'bg-[#F5A623]' },
  { text: 'Adaptive content based on your interests and goals', color: 'bg-[#1A2E6E]' }
];

export const About = () => {
  const cms = useCmsContent('about');
  const { t, tList } = useLanguage();

  const heroImageRaw   = cms.get('hero', 'image', ABOUT_IMAGE_FB);
  const heroImage      = heroImageRaw === ABOUT_IMAGE_PREVIOUS ? ABOUT_IMAGE_FB : heroImageRaw;
  const methodImageRaw = cms.get('methodology', 'image', METHOD_IMAGE_FB);
  const methodImage    = [METHOD_IMAGE_PREVIOUS, METHOD_IMAGE_LEGACY].includes(methodImageRaw)
    ? METHOD_IMAGE_FB
    : methodImageRaw;
  const heroBadge      = cms.get('hero', 'badge',  t('about.hero.badge'));
  const heroTitle      = cms.get('hero', 'title',  t('about.hero.title'));
  const heroBody1      = cms.get('hero', 'body1',  t('about.hero.body1'));
  const heroBody2      = cms.get('hero', 'body2',  t('about.hero.body2'));
  const heroStat       = cms.get('hero', 'stat',   t('about.hero.stat'));
  const heroAvatars    = [
    cms.get('hero', 'person1_avatar', ABOUT_AVATARS_FB[0]),
    cms.get('hero', 'person2_avatar', ABOUT_AVATARS_FB[1]),
    cms.get('hero', 'person3_avatar', ABOUT_AVATARS_FB[2]),
  ].filter(Boolean);
  const methodBadge    = cms.get('methodology', 'badge', t('about.methodology.badge'));
  const methodTitle    = cms.get('methodology', 'title', t('about.methodology.title'));
  const methodBody     = cms.get('methodology', 'body',  t('about.methodology.body'));
  const ctaTitle       = cms.get('cta', 'title',   t('about.ctaSection.title'));
  const ctaBody        = cms.get('cta', 'body',    t('about.ctaSection.body'));
  const ctaPrimary     = cms.get('cta', 'cta_primary',   t('about.ctaSection.primary'));
  const ctaSecondary   = cms.get('cta', 'cta_secondary', t('about.ctaSection.secondary'));

  const localizedDiff = tList('about.differentiatorsList');
  const cmsDiff = cms.getList('differentiators', []);
  const differentiators = DEFAULT_DIFF.map((d, i) => ({ ...d, text: cmsDiff[i]?.text || localizedDiff[i]?.text || d.text }));

  const localizedValues = tList('about.valuesList');
  const cmsValues = cms.getList('values', []);
  const values = DEFAULT_VALUES.map((v, i) => ({
    ...v,
    title:       cmsValues[i]?.title       || localizedValues[i]?.title       || v.title,
    description: cmsValues[i]?.description || localizedValues[i]?.description || v.description,
  }));

  const localizedMethodPts = tList('about.methodPointsList');
  const cmsMethodPts = cms.getList('methodology_points', []);
  const methodPoints = DEFAULT_METHOD_POINTS.map((p, i) => ({ ...p, text: cmsMethodPts[i]?.text || localizedMethodPts[i]?.text || p.text }));

  return (
    <main className="pt-24" data-testid="about-page">
      {/* Hero Section */}
      <section className="section-padding bg-cream-gradient">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <span className="badge-navy mb-4 inline-block">
                {heroBadge}
              </span>
              <h1 className="font-sora text-4xl md:text-5xl font-bold text-[#1A2E6E] mb-6">
                {parseHighlight(heroTitle)}
              </h1>
              <p className="text-lg text-[#1A2E6E]/80 leading-relaxed mb-6">
                {heroBody1}
              </p>
              <p className="text-[#1A2E6E]/70 leading-relaxed">
                {heroBody2}
              </p>
              <div className="flex flex-col gap-4 mt-8 sm:flex-row sm:items-center">
                <div className="flex -space-x-3">
                  {heroAvatars.map((avatar, index) => (
                    <img
                      key={`${avatar}-${index}`}
                      src={avatar}
                      alt={t('common.studentAlt', { index: index + 1 })}
                      className="w-10 h-10 rounded-full border-2 border-white object-cover"
                    />
                  ))}
                </div>
                <p className="text-sm text-[#1A2E6E]/70">
                  {heroStat}
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="relative"
            >
              <div className="absolute -top-4 -right-4 w-full h-full bg-[#F5A623]/20 rounded-3xl" />
              <div className="absolute -bottom-4 -left-4 w-full h-full bg-[#4CAF50]/20 rounded-3xl" />
              <div className="photo-container relative aspect-[3/2] rounded-3xl">
                <img
                  src={heroImage}
                  alt={t('about.heroImageAlt')}
                  className="photo-fit-card"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What Makes Us Different */}
      <section className="section-padding bg-warm-texture">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto text-center mb-12"
          >
            <span className="badge-red mb-4 inline-block">{t('about.whatMakesUsDifferentBadge')}</span>
            <h2 className="font-sora text-3xl md:text-4xl font-bold text-[#1A2E6E] mb-4">
              {t('about.whatMakesUsDifferentTitle')}
            </h2>
            <p className="text-[#1A2E6E]/70 text-lg">
              {t('about.whatMakesUsDifferentSubtitle')}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto">
            {differentiators.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex items-start gap-3 p-5 rounded-xl glass-card hover:shadow-md transition-all"
              >
                <item.icon size={20} className={`${item.color} mt-0.5 flex-shrink-0`} />
                <span className="text-[#1A2E6E] font-medium">{item.text}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Teaching Methodology */}
      <section className="section-padding bg-paper-lines">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="order-2 lg:order-1"
            >
              <span className="badge-green mb-4 inline-block">
                {methodBadge}
              </span>
              <h2 className="font-sora text-3xl md:text-4xl font-bold text-[#1A2E6E] mb-6">
                {parseHighlight(methodTitle)}
              </h2>
              <p className="text-[#1A2E6E]/70 leading-relaxed mb-6">
                {methodBody}
              </p>
              <ul className="space-y-4">
                {methodPoints.map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className={`w-2 h-2 rounded-full ${item.color} mt-2.5 flex-shrink-0`} />
                    <span className="text-[#1A2E6E]/80">{item.text}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="order-1 lg:order-2"
            >
              <div className="photo-container aspect-video">
                <img
                  src={methodImage}
                  alt={t('about.methodImageAlt')}
                  className="photo-fit-card"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="section-padding bg-watercolor-soft">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="badge-orange mb-4 inline-block">{t('about.coreValuesBadge')}</span>
            <h2 className="font-sora text-3xl md:text-4xl font-bold text-[#1A2E6E] mb-4">
              {t('about.coreValuesTitle')}
            </h2>
            <p className="text-[#1A2E6E]/70 text-lg">
              {t('about.coreValuesSubtitle')}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="glass-card p-6 sm:p-8 card-accent-hover card-accent-navy hover:shadow-xl transition-all duration-300"
              >
                <div className={`w-14 h-14 rounded-xl ${value.color} flex items-center justify-center mb-6 shadow-lg`}>
                  <value.icon size={28} className="text-white" />
                </div>
                <h3 className="font-sora font-semibold text-xl text-[#1A2E6E] mb-2">
                  {value.title}
                </h3>
                <p className="text-[#1A2E6E]/70">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-gradient-to-br from-[#1A2E6E] to-[#2A4A8E]">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-2xl mx-auto"
          >
            <h2 className="font-sora text-3xl md:text-4xl font-bold text-white mb-4">
              {parseHighlight(ctaTitle)}
            </h2>
            <p className="text-white/80 text-lg mb-8">
              {ctaBody}
            </p>
            <div className="flex flex-col justify-center gap-4 sm:flex-row sm:flex-wrap">
              <Link
                to="/book-trial"
                className="btn-primary inline-flex w-full items-center justify-center gap-2 sm:w-auto"
                data-testid="about-book-trial-btn"
              >
                {ctaPrimary} <ArrowRight size={18} />
              </Link>
              <Link
                to="/level-test"
                className="w-full rounded-full border-2 border-white/30 bg-transparent px-8 py-3.5 text-center font-semibold text-white transition-all duration-300 hover:bg-white/10 sm:w-auto"
                data-testid="about-level-test-btn"
              >
                {ctaSecondary}
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
};
